import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useMsal } from '@azure/msal-react'
import api from '@/services/api'
import { AVATAR_SOURCE_MAX_BYTES, squareDataUrl } from '@/utils/avatarImage'
import { devSessionAccount, isAzureAuthEnabled } from '@/auth/authMode'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import type { LucideIcon } from 'lucide-react'
import {
  User,
  Shield,
  Users,
  Activity,
  Plug,
  Database,
  Settings,
  Camera,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  Plus,
  Copy,
  Search,
  RefreshCw,
  Clock,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  Lock,
  Eye as EyeIcon,
  Mail,
  Tags,
} from 'lucide-react'
import DemoDataSettingsTab from '@/components/settings/DemoDataSettingsTab'
import SlaSettingsTab from '@/components/settings/SlaSettingsTab'
import PasskeySecurityPanel from '@/components/settings/PasskeySecurityPanel'
import TotpPanel, { TrialTwoFactorToggle } from '@/components/settings/TotpPanel'
import OrgChartSettingsPanel from '@/components/settings/OrgChartSettingsPanel'
import ReferenceDataSettingsTab from '@/components/settings/ReferenceDataSettingsTab'
import IntegrationsHub from '@/components/settings/IntegrationsHub'
import DatabaseSettingsTab from '@/components/settings/DatabaseSettingsTab'
import RbacSettingsTab from '@/components/settings/RbacSettingsTab'
import AdminUsersSettingsTab from '@/components/settings/AdminUsersSettingsTab'
import AdminActivitySettingsTab from '@/components/settings/AdminActivitySettingsTab'
import ActiveSessionsPanel from '@/components/settings/ActiveSessionsPanel'
import TrialExperiencePreview from '@/components/shared/TrialExperiencePreview'
import { WORKFLOW_ORDER, getWorkflowMeta } from '@/config/workflowModules'
import type { ModuleGroup } from '@/config/workflowModules'
import type { PlatformModuleId } from '@/types'

/** Matches backend `PERMANENT_SUPER_ADMIN_EMAIL` — ProtonMail / SMTP UI is shown only for this account. */
const PERMANENT_SUPER_ADMIN_EMAIL = 'edward@helios-fintech.com'

const DEPARTMENTS = ['Originations', 'Underwriting', 'Asset Management', 'Trading', 'Risk', 'Executive'] as const

const MODULES = [
  'Command Center', 'Pipeline', 'Intake', 'Screening', 'Underwriting',
  'Committee', 'Closing', 'Surveillance', 'Asset Management', 'Valuation',
  'Market Intelligence', 'Reports',
] as const

// ─── Sidebar Tabs ────────────────────────────────────────────────────────────

type TabId = 'profile' | 'security' | 'permissions' | 'users' | 'trialLicenses' | 'activity' | 'integrations' | 'database' | 'referenceData' | 'demoData' | 'sla' | 'platform'

const TABS: { id: TabId; label: string; icon: LucideIcon }[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'permissions', label: 'Role Permissions', icon: Shield },
  { id: 'users', label: 'User Management', icon: Users },
  { id: 'trialLicenses', label: 'Trial Licenses', icon: Lock },
  { id: 'activity', label: 'Activity & Audit', icon: Activity },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'database', label: 'Database & Backups', icon: Database },
  { id: 'referenceData', label: 'Reference Data', icon: Tags },
  { id: 'demoData', label: 'Demo Data', icon: EyeIcon },
  { id: 'sla', label: 'Assignment SLA', icon: Clock },
  { id: 'platform', label: 'Platform Settings', icon: Settings },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    Inactive: 'bg-stone-500/15 text-stone-400 border-stone-500/20',
    inactive: 'bg-stone-500/15 text-stone-400 border-stone-500/20',
    Locked: 'bg-red-500/15 text-red-400 border-red-500/20',
    Success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    Failed: 'bg-red-500/15 text-red-400 border-red-500/20',
    'MFA Challenge': 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    Completed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    Paused: 'bg-stone-500/15 text-stone-400 border-stone-500/20',
    scheduled: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    terminated: 'bg-red-500/15 text-red-400 border-red-500/20',
    expired: 'bg-red-500/15 text-red-400 border-red-500/20',
    standard: 'bg-stone-500/15 text-stone-400 border-stone-500/20',
    'Pending Agreement': 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    connected: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    pending: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    disconnected: 'bg-red-500/15 text-red-400 border-red-500/20',
  }
  const cls = map[status] || 'bg-stone-500/15 text-stone-400 border-stone-500/20'
  const label = (
    status === 'connected' ? 'Connected'
      : status === 'pending' ? 'Pending Setup'
        : status === 'disconnected' ? 'Disconnected'
          : status === 'scheduled' ? 'Scheduled'
            : status === 'terminated' ? 'Terminated'
              : status === 'expired' ? 'Expired'
                : status === 'active' ? 'Active'
                  : status === 'inactive' ? 'Inactive'
                    : status
  )
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${status === 'Active' || status === 'Success' || status === 'Completed' || status === 'connected' ? 'bg-emerald-400' : status === 'Failed' || status === 'Locked' || status === 'disconnected' ? 'bg-red-400' : status === 'MFA Challenge' || status === 'pending' ? 'bg-amber-400' : 'bg-stone-400'}`} />
      {label}
    </span>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="mb-3 text-[13px] font-semibold text-stone-100">{children}</h3>
}

function FormLabel({ children }: { children: React.ReactNode }) {
  return <label className="mb-1 block text-[11px] font-medium text-stone-400">{children}</label>
}

function FormInput({
  type = 'text',
  value,
  defaultValue = '',
  onChange,
  placeholder = '',
  disabled = false,
  'aria-label': ariaLabel,
}: {
  type?: string
  value?: string
  defaultValue?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  disabled?: boolean
  'aria-label'?: string
}) {
  const common = 'input w-full text-[12px] disabled:opacity-40'
  if (value !== undefined) {
    return (
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        className={common}
      />
    )
  }
  return (
    <input
      type={type}
      defaultValue={defaultValue}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel}
      className={common}
    />
  )
}

function FormSelect({
  options,
  defaultValue,
  value,
  onChange,
}: {
  options: readonly string[]
  defaultValue?: string
  value?: string
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void
}) {
  return (
    <div className="relative">
      <select
        value={value}
        defaultValue={value === undefined ? defaultValue : undefined}
        onChange={onChange}
        className="input w-full appearance-none pr-8 text-[12px]"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <ChevronDown size={12} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-stone-500" />
    </div>
  )
}

// ─── Tab Content Components ──────────────────────────────────────────────────

function profileInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '—'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** The notifications a person can ask for, and the default answer. */
const NOTIFICATION_KINDS = [
  { key: 'dealStage', label: 'Deal stage changes', desc: 'Notify when a deal moves between pipeline stages' },
  { key: 'triggerAlerts', label: 'Trigger alerts', desc: 'DSCR, LTV, and covenant breach notifications' },
  { key: 'committee', label: 'Committee decisions', desc: 'Approval, conditional, or decline notifications' },
  { key: 'reports', label: 'Report generation', desc: 'Notification when scheduled reports are ready' },
  { key: 'system', label: 'System alerts', desc: 'Backup failures, integration errors, security events' },
] as const

type NotificationKey = (typeof NOTIFICATION_KINDS)[number]['key']

const DEFAULT_NOTIFICATIONS: Record<NotificationKey, boolean> = {
  dealStage: true,
  triggerAlerts: true,
  committee: true,
  reports: false,
  system: true,
}

function ProfileTab() {
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)
  const [emailReadOnly, setEmailReadOnly] = useState('')

  const [fullName, setFullName] = useState('')
  const [title, setTitle] = useState('')
  const [department, setDepartment] = useState('Executive')
  const [phone, setPhone] = useState('')
  const [officeLocation, setOfficeLocation] = useState('')
  const [defaultCurrency, setDefaultCurrency] = useState('USD')
  const [defaultModule, setDefaultModule] = useState('Command Center')
  const [timezone, setTimezone] = useState('America/New_York (EST)')
  const [dateFormat, setDateFormat] = useState('YYYY-MM-DD')
  const [notifications, setNotifications] = useState<Record<NotificationKey, boolean>>(
    DEFAULT_NOTIFICATIONS,
  )
  const [avatar, setAvatar] = useState('')
  const [avatarBusy, setAvatarBusy] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const avatarInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoadError(null)
      try {
        const res = await api.get<{ data: Record<string, string> }>('/settings/profile')
        const d = res.data?.data ?? {}
        if (cancelled) return
        setFullName(d.fullName ?? '')
        setTitle(d.title ?? '')
        setDepartment(d.department ?? 'Executive')
        setPhone(d.phone ?? '')
        setOfficeLocation(d.officeLocation ?? '')
        setDefaultCurrency(d.defaultCurrency ?? 'USD')
        setDefaultModule(d.defaultModule ?? 'Command Center')
        setTimezone(d.timezone ?? 'America/New_York (EST)')
        setDateFormat(d.dateFormat ?? 'YYYY-MM-DD')
        setEmailReadOnly(d.email ?? '')
        setAvatar(d.avatar ?? '')
        const stored = d.notifications as unknown
        if (stored && typeof stored === 'object') {
          const raw = stored as Record<string, boolean>
          setNotifications(
            Object.fromEntries(
              NOTIFICATION_KINDS.map((n) => [n.key, Boolean(raw[n.key] ?? DEFAULT_NOTIFICATIONS[n.key])]),
            ) as Record<NotificationKey, boolean>,
          )
        }
      } catch {
        if (!cancelled) setLoadError('Could not load profile. Check that you are signed in and the API is reachable.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setSaveOk(false)
    setLoadError(null)
    try {
      await api.patch('/settings/profile', {
        data: {
          fullName,
          title,
          department,
          phone,
          officeLocation,
          defaultCurrency,
          defaultModule,
          timezone,
          dateFormat,
          avatar,
          notifications,
        },
      })
      setSaveOk(true)
      setTimeout(() => setSaveOk(false), 4000)
    } catch {
      setLoadError('Save failed. Check your connection and permissions.')
    } finally {
      setSaving(false)
    }
  }, [fullName, title, department, phone, officeLocation, defaultCurrency, defaultModule, timezone, dateFormat, avatar, notifications])

  /** Take whatever the person picked, square it, shrink it to AVATAR_PX and
   *  save it with the profile.
   *
   *  The photo is downscaled and re-encoded in the browser rather than posted
   *  as-is: a phone camera JPEG is 3-6MB, and the avatar is read on every page
   *  load. A centre crop keeps a portrait recognisable at 20px without asking
   *  anyone to frame it. The write goes out immediately — a photo that needed
   *  a separate "Save Changes" was the sort of thing people lose.
   */
  const onPickPhoto = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      // Clear the input so picking the same file twice still fires a change.
      event.target.value = ''
      if (!file) return
      setAvatarError(null)
      if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
        setAvatarError('Choose a PNG, JPEG or WebP image.')
        return
      }
      if (file.size > AVATAR_SOURCE_MAX_BYTES) {
        setAvatarError('That image is over 12MB. Choose a smaller one.')
        return
      }
      setAvatarBusy(true)
      try {
        const dataUrl = await squareDataUrl(file)
        setAvatar(dataUrl)
        await api.patch('/settings/profile', { data: { avatar: dataUrl } })
        setSaveOk(true)
        setTimeout(() => setSaveOk(false), 4000)
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status
        setAvatarError(
          status === 413
            ? 'The image was still too large after resizing. Try a simpler photo.'
            : 'The photo could not be saved. Check your connection and try again.',
        )
        setAvatar('')
      } finally {
        setAvatarBusy(false)
      }
    },
    [],
  )

  const onRemovePhoto = useCallback(async () => {
    setAvatarError(null)
    setAvatarBusy(true)
    try {
      setAvatar('')
      await api.patch('/settings/profile', { data: { avatar: '' } })
    } catch {
      setAvatarError('The photo could not be removed.')
    } finally {
      setAvatarBusy(false)
    }
  }, [])

  const initials = profileInitials(fullName)

  return (
    <div className="space-y-6">
      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
          <AlertCircle size={14} /> {loadError}
        </div>
      )}
      {loading ? (
        <div className="text-[12px] text-stone-500">Loading profile…</div>
      ) : null}

      <SectionHeading>Personal Information</SectionHeading>
      <div className="flex items-start gap-5">
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-amber-300/20 bg-gradient-to-br from-black to-[#161616] text-2xl font-bold text-amber-200">
            {avatar ? (
              <img src={avatar} alt="" className="h-full w-full object-cover" />
            ) : (
              initials
            )}
          </div>
          <input
            ref={avatarInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onPickPhoto}
            data-testid="avatar-input"
          />
          <button
            type="button"
            disabled={avatarBusy || loading}
            onClick={() => avatarInput.current?.click()}
            className="flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200 disabled:opacity-50"
          >
            <Camera size={10} /> {avatarBusy ? 'Saving…' : avatar ? 'Change Photo' : 'Add Photo'}
          </button>
          {avatar && !avatarBusy ? (
            <button
              type="button"
              onClick={onRemovePhoto}
              className="text-[10px] text-stone-500 hover:text-stone-300"
            >
              Remove
            </button>
          ) : null}
          {avatarError ? (
            <div className="max-w-[8rem] text-center text-[10px] text-red-300">{avatarError}</div>
          ) : null}
        </div>
        <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-3">
          <div>
            <FormLabel>Full Name</FormLabel>
            <FormInput value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={loading} />
          </div>
          <div>
            <FormLabel>Email</FormLabel>
            <FormInput type="email" value={emailReadOnly} disabled placeholder="From your account" />
          </div>
          <div>
            <FormLabel>Title</FormLabel>
            <FormInput value={title} onChange={(e) => setTitle(e.target.value)} disabled={loading} />
          </div>
          <div>
            <FormLabel>Department</FormLabel>
            <FormSelect
              options={DEPARTMENTS}
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            />
          </div>
          <div>
            <FormLabel>Phone</FormLabel>
            <FormInput type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={loading} />
          </div>
          <div>
            <FormLabel>Office Location</FormLabel>
            <FormInput value={officeLocation} onChange={(e) => setOfficeLocation(e.target.value)} disabled={loading} />
          </div>
        </div>
      </div>

      <div className="h-px bg-amber-400/10" />

      <SectionHeading>Notifications</SectionHeading>
      {/* A notification preference is a fact about a person, not about the
          platform: two people on one deployment want different mail. They are
          stored with the rest of this profile and saved by the same button. */}
      <div className="max-w-md space-y-2.5">
        {NOTIFICATION_KINDS.map((n) => (
          <div
            key={n.key}
            className="flex items-center justify-between rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2.5"
          >
            <div>
              <div className="text-[12px] font-medium text-stone-200">{n.label}</div>
              <div className="text-[10px] text-stone-500">{n.desc}</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={notifications[n.key]}
              aria-label={n.label}
              disabled={loading}
              onClick={() => setNotifications((prev) => ({ ...prev, [n.key]: !prev[n.key] }))}
              className="flex-shrink-0 disabled:opacity-50"
            >
              {notifications[n.key] ? (
                <ToggleRight size={24} className="text-amber-300" />
              ) : (
                <ToggleLeft size={24} className="text-stone-600" />
              )}
            </button>
          </div>
        ))}
      </div>

      <SectionHeading>Preferences</SectionHeading>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 lg:grid-cols-3">
        <div>
          <FormLabel>Default Currency</FormLabel>
          <FormSelect
            options={['USD', 'EUR', 'GBP', 'JPY', 'CHF']}
            value={defaultCurrency}
            onChange={(e) => setDefaultCurrency(e.target.value)}
          />
        </div>
        <div>
          <FormLabel>Default Module on Login</FormLabel>
          <FormSelect options={MODULES} value={defaultModule} onChange={(e) => setDefaultModule(e.target.value)} />
        </div>
        <div>
          <FormLabel>Theme</FormLabel>
          <div className="input flex w-full items-center gap-2 text-[12px] opacity-60">
            <Lock size={10} className="text-stone-500" /> Dark (System Default)
          </div>
        </div>
        <div>
          <FormLabel>Timezone</FormLabel>
          <FormSelect
            options={[
              'America/New_York (EST)',
              'America/Chicago (CST)',
              'America/Denver (MST)',
              'America/Los_Angeles (PST)',
              'Europe/London (GMT)',
              'Asia/Tokyo (JST)',
            ]}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          />
        </div>
        <div>
          <FormLabel>Date Format</FormLabel>
          <FormSelect
            options={['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'DD-MMM-YYYY']}
            value={dateFormat}
            onChange={(e) => setDateFormat(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          type="button"
          className="btn-primary text-[12px] disabled:opacity-50"
          disabled={loading || saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        {saveOk ? (
          <span className="flex items-center gap-1 text-[11px] text-emerald-400">
            <CheckCircle2 size={12} /> Saved
          </span>
        ) : null}
      </div>
    </div>
  )
}

function SecurityTab() {
  const { isLocalAuth } = useLocalAuth()
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordBusy, setPasswordBusy] = useState(false)
  const [passwordOk, setPasswordOk] = useState<string | null>(null)
  const [passwordErr, setPasswordErr] = useState<string | null>(null)

  const handlePasswordUpdate = useCallback(async () => {
    setPasswordOk(null)
    setPasswordErr(null)
    if (newPassword !== confirmPassword) {
      setPasswordErr('New password and confirmation do not match.')
      return
    }
    setPasswordBusy(true)
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordOk('Password updated.')
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setPasswordErr(typeof detail === 'string' ? detail : 'Could not update password.')
    } finally {
      setPasswordBusy(false)
    }
  }, [confirmPassword, currentPassword, newPassword])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-amber-400/10 bg-black/20 p-4 space-y-4">
          <SectionHeading>Password</SectionHeading>
          {isLocalAuth ? (
            <>
              <p className="text-[11px] leading-relaxed text-stone-500">
                Password login remains available as a fallback when passkeys are unavailable.
              </p>
              {passwordErr ? (
                <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
                  <AlertCircle size={14} /> {passwordErr}
                </div>
              ) : null}
              {passwordOk ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-300">
                  <CheckCircle2 size={14} /> {passwordOk}
                </div>
              ) : null}
              <div className="grid gap-3">
                <div>
                  <FormLabel>Current Password</FormLabel>
                  <div className="relative">
                    <FormInput
                      type={showCurrent ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                    <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300">
                      {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div>
                  <FormLabel>New Password</FormLabel>
                  <div className="relative">
                    <FormInput
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                    <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300">
                      {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div>
                  <FormLabel>Confirm New Password</FormLabel>
                  <FormInput
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                </div>
                <div className="pt-1">
                  <button
                    type="button"
                    className="btn-primary w-full text-[12px] disabled:opacity-50"
                    disabled={passwordBusy || !currentPassword || !newPassword || !confirmPassword}
                    onClick={() => void handlePasswordUpdate()}
                  >
                    {passwordBusy ? 'Updating…' : 'Update Password'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p className="text-[11px] leading-relaxed text-stone-500">
              This session uses Microsoft sign-in. There is no local password stored on this
              platform to change. Ask an administrator to reset a local account from User Management.
            </p>
          )}
        </div>

        <div className="rounded-lg border border-amber-400/10 bg-black/20 p-4 space-y-4">
          <SectionHeading>Authenticator (MFA)</SectionHeading>
          <TotpPanel />
        </div>

        <div className="rounded-lg border border-amber-400/10 bg-black/20 p-4 space-y-4">
          <SectionHeading>Passkey</SectionHeading>
          <PasskeySecurityPanel embedded />
        </div>
      </div>

      <div className="h-px bg-amber-400/10" />

      <SectionHeading>Active Sessions</SectionHeading>
      <ActiveSessionsPanel />
    </div>
  )
}

function UserManagementTab() {
  return (
    <div className="space-y-5">
      {/* Permissions are edited per person, from the row that names them —
          see UserPermissionsDialog. The org chart is structure, not access,
          and rides along here because it is about the same people. */}
      <AdminUsersSettingsTab />
      <OrgChartSettingsPanel />
    </div>
  )
}

type TrialLicense = {
  id: string
  email: string
  name: string
  role: string
  status: string
  // Whether the holder can sign in right now, by the server's own login gates.
  // A status of 'terminated' with a future expiry is still a lockout.
  can_sign_in?: boolean
  sign_in_blocked_reason?: string | null
  is_active: boolean
  is_trial: boolean
  totp_enabled: boolean
  trial_started_at: string | null
  trial_expires_at: string | null
  trial_created_by: string | null
  trial_termination_scheduled_at: string | null
  trial_terminated_at: string | null
  trial_termination_reason: string | null
  last_login_at: string | null
  created_at: string
  require_trial_agreement: boolean
  require_nda: boolean
  trial_agreement_required: boolean
  trial_agreement_accepted_at: string | null
  trial_agreement_version: string | null
  nda_required: boolean
  nda_accepted_at: string | null
  nda_version: string | null
  // null = unrestricted (role decides); a list = exactly those modules.
  module_access: string[] | null
  // Role ∩ module_access, resolved server-side — what the holder actually sees.
  visible_modules: string[]
}

/**
 * A stored instant as a `datetime-local` value in the operator's own clock.
 *
 * The API returns UTC instants, with an offset (Postgres) or naive (SQLite,
 * UTC by construction). Slicing the string put UTC digits into a local-time
 * input, so an unchanged Save moved the expiry by the browser's UTC offset.
 *
 * Round-trip uses calendar parts, never `Date.parse` of a zoneless
 * `YYYY-MM-DDTHH:mm` string — that form is engine/locale-defined and is
 * `NaN` in jsdom/CI.
 */
const DATETIME_LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/

export function parseDateTimeLocal(value: string): Date | null {
  const m = DATETIME_LOCAL_RE.exec((value || '').trim())
  if (!m) return null
  const instant = new Date(
    Number(m[1]), Number(m[2]) - 1, Number(m[3]),
    Number(m[4]), Number(m[5]), Number(m[6] || 0),
  )
  return Number.isNaN(instant.getTime()) ? null : instant
}

export function isoToLocalInput(value: string | null | undefined): string {
  if (!value) return ''
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value)
  const instant = new Date(hasZone ? value : `${value}Z`)
  if (Number.isNaN(instant.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${instant.getFullYear()}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}T${pad(instant.getHours())}:${pad(instant.getMinutes())}`
}

/** A `datetime-local` value (the operator's clock) as a UTC instant. */
export function localInputToIso(value: string): string | null {
  if (!value) return null
  const instant = parseDateTimeLocal(value)
  return instant ? instant.toISOString() : null
}

type TrialDocumentSection = {
  heading: string
  body: string[]
}

type TrialDocument = {
  id: number
  document_type: string
  title: string
  version: string
  sections: TrialDocumentSection[]
  is_active: boolean
  created_by: string | null
  created_at: string
}

type CreatedTrialLicense = {
  id: string
  email: string
  name: string
  role: string
  password: string
  expires_at: string
  totp_secret: string | null
  totp_provisioning_uri: string | null
  email_sent: boolean
  email_error: string | null
}

type TrialPasswordReset = {
  id: string
  email: string
  name: string
  password: string
  generated: boolean
}

const MODULE_GROUP_LABELS: Record<ModuleGroup, string> = {
  dashboards: 'Dashboards',
  'pre-closing': 'Pre-Closing',
  'post-closing': 'Post-Closing',
  tools: 'Tools',
}

/**
 * Which modules a license may reach.
 *
 * `value === null` means unrestricted — the role decides, which is what every
 * license carries until an operator narrows it. That state is deliberately
 * distinct from "every box ticked": unrestricted follows the role as it
 * changes, while an explicit full list freezes today's answer.
 */
function ModuleAccessEditor({
  value,
  visibleModules,
  onChange,
  disabled,
}: {
  value: string[] | null
  visibleModules: string[]
  onChange: (next: string[] | null) => void
  disabled?: boolean
}) {
  const restricted = value !== null
  const selected = new Set(value ?? [])
  const visible = new Set(visibleModules)

  // Derived from the nav order rather than listed by hand: a hardcoded list
  // silently drops a whole group's checkboxes when a new group is added, so a
  // restricted license loses those modules with no control to put them back.
  const groups: ModuleGroup[] = [...new Set(WORKFLOW_ORDER.map((id) => getWorkflowMeta(id).group))]

  const toggle = (moduleId: PlatformModuleId) => {
    if (!restricted) return
    const next = new Set(selected)
    if (next.has(moduleId)) next.delete(moduleId)
    else next.add(moduleId)
    onChange(WORKFLOW_ORDER.filter((id) => next.has(id)))
  }

  return (
    <div className="rounded-lg border border-amber-400/10 bg-black/20 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[12px] font-medium text-stone-200">Module Access</div>
          <div className="mt-0.5 text-[10px] text-stone-500">
            {restricted
              ? `${selected.size} of ${WORKFLOW_ORDER.length} modules licensed`
              : 'Unrestricted — this license sees every module its role allows'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {restricted ? (
            <>
              <button
                className="text-[10px] text-amber-300 hover:text-amber-200 disabled:opacity-40"
                disabled={disabled}
                onClick={() => onChange([...WORKFLOW_ORDER])}
                type="button"
              >
                Select all
              </button>
              <button
                className="text-[10px] text-amber-300 hover:text-amber-200 disabled:opacity-40"
                disabled={disabled}
                onClick={() => onChange([])}
                type="button"
              >
                Clear all
              </button>
              <button
                className="text-[10px] text-stone-500 hover:text-stone-300 disabled:opacity-40"
                disabled={disabled}
                onClick={() => onChange(null)}
                type="button"
              >
                Remove restriction
              </button>
            </>
          ) : (
            <button
              className="text-[10px] text-amber-300 hover:text-amber-200 disabled:opacity-40"
              disabled={disabled}
              onClick={() => onChange([...WORKFLOW_ORDER])}
              type="button"
            >
              Restrict modules
            </button>
          )}
        </div>
      </div>

      {restricted ? (
        <>
          {selected.size === 0 ? (
            <div className="mt-2 rounded-md border border-amber-500/25 bg-amber-500/10 px-2.5 py-1.5 text-[10px] text-amber-300">
              No modules selected — this license will sign in to an empty platform.
            </div>
          ) : null}
          <div className="mt-3 space-y-3">
            {groups.map((group) => {
              const ids = WORKFLOW_ORDER.filter((id) => getWorkflowMeta(id).group === group)
              return (
                <div key={group}>
                  <div className="mb-1.5 text-[9px] font-medium uppercase tracking-[0.2em] text-stone-600">
                    {MODULE_GROUP_LABELS[group]}
                  </div>
                  <div className="grid gap-1 sm:grid-cols-2">
                    {ids.map((moduleId) => {
                      const checked = selected.has(moduleId)
                      // Checked but not actually visible => the ROLE denies it.
                      // Saying so beats letting the operator believe a ticked
                      // box put the module on the holder's screen.
                      const blockedByRole = checked && !visible.has(moduleId)
                      return (
                        <label
                          key={moduleId}
                          className="flex items-center gap-2 rounded border border-amber-400/[0.07] bg-black/25 px-2 py-1 text-[11px] text-stone-300"
                        >
                          <input
                            checked={checked}
                            className="h-3.5 w-3.5 rounded border-amber-300/30 bg-black/60 accent-amber-400"
                            disabled={disabled}
                            onChange={() => toggle(moduleId)}
                            type="checkbox"
                          />
                          <span className="flex-1 truncate">{getWorkflowMeta(moduleId).label}</span>
                          {blockedByRole ? (
                            <span className="text-[9px] text-amber-400/80" title="The role denies this module, so it stays hidden regardless">
                              role
                            </span>
                          ) : null}
                        </label>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : null}
    </div>
  )
}

/** Set a trial password to whatever the operator types, or generate one. */
function TrialPasswordResetPanel({
  license,
  busy,
  issued,
  onSubmit,
  onCopy,
  onDismiss,
}: {
  license: TrialLicense
  busy: boolean
  issued: TrialPasswordReset | null
  onSubmit: (password?: string) => void
  onCopy: (value: string, label: string) => void
  onDismiss: () => void
}) {
  const [password, setPassword] = useState('')
  const [reveal, setReveal] = useState(false)

  // Mirrors app/core/passwords.py, for immediate feedback only — the server
  // enforces the same floor regardless of what this component allows.
  const tooShort = password.length > 0 && password.length < 12
  const missingClasses =
    password.length >= 12 &&
    !(/[A-Z]/.test(password) && /[a-z]/.test(password) && /\d/.test(password))
  const invalid = tooShort || missingClasses

  return (
    <div className="rounded-lg border border-amber-400/10 bg-black/20 p-3">
      <div className="text-[12px] font-medium text-stone-200">Reset Password</div>
      <div className="mt-0.5 text-[10px] text-stone-500">
        Type a password you can read out on a call, or generate one. Takes effect immediately for{' '}
        <span className="text-stone-400">{license.email}</span>.
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <input
            autoComplete="new-password"
            className="w-full rounded border border-amber-400/20 bg-black/40 px-2.5 py-1.5 pr-14 font-mono text-[12px] text-stone-100 outline-none placeholder:font-sans placeholder:text-stone-600 focus:border-amber-400/40"
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && password && !invalid && !busy) onSubmit(password)
            }}
            placeholder="Type a password, or generate one"
            type={reveal ? 'text' : 'password'}
            value={password}
          />
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-stone-500 hover:text-stone-300"
            onClick={() => setReveal((v) => !v)}
            type="button"
          >
            {reveal ? 'Hide' : 'Show'}
          </button>
        </div>
        <button
          className="btn-primary text-[11px] disabled:opacity-40"
          disabled={busy || !password || invalid}
          onClick={() => onSubmit(password)}
          type="button"
        >
          {busy ? 'Setting…' : 'Set Password'}
        </button>
        <button
          className="btn-secondary text-[11px] disabled:opacity-40"
          disabled={busy}
          onClick={() => onSubmit(undefined)}
          type="button"
        >
          Generate instead
        </button>
      </div>

      {invalid ? (
        <p className="mt-1.5 text-[10px] text-red-400">
          {tooShort
            ? 'At least 12 characters.'
            : 'Needs an uppercase letter, a lowercase letter, and a digit.'}
        </p>
      ) : null}

      {issued && issued.id === license.id ? (
        <div className="mt-2.5 rounded-md border border-amber-400/25 bg-amber-400/[0.06] px-2.5 py-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">
                New password for {issued.email}
              </div>
              <div className="mt-1 font-mono text-[12px] text-amber-200 break-all select-all">
                {issued.password}
              </div>
              <div className="mt-1 text-[10px] text-stone-500">
                Shown once — it cannot be retrieved later. Any signed-in session keeps working
                until its token expires; deactivate the license to cut one now.
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <button
                className="text-[10px] text-amber-300 hover:text-amber-200"
                onClick={() => onCopy(issued.password, 'Password')}
                type="button"
              >
                <Copy size={10} className="mr-1 inline" />
                Copy
              </button>
              <button
                className="text-[10px] text-stone-500 hover:text-stone-300"
                onClick={onDismiss}
                type="button"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function TrialLicensesTab() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [licenses, setLicenses] = useState<TrialLicense[]>([])
  const [createdLicenses, setCreatedLicenses] = useState<CreatedTrialLicense[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [createName, setCreateName] = useState('')
  const [createEmail, setCreateEmail] = useState('')
  // Analyst is the Step-1 working role; viewer stays issuable (read-only).
  const [createRole, setCreateRole] = useState('analyst')
  const [createHours, setCreateHours] = useState('168')
  const [createRequireTotp, setCreateRequireTotp] = useState(true)
  const [createRequireAgreement, setCreateRequireAgreement] = useState(true)
  const [createRequireNda, setCreateRequireNda] = useState(true)
  const [createModuleAccess, setCreateModuleAccess] = useState<string[] | null>(null)
  const [documents, setDocuments] = useState<TrialDocument[]>([])
  const [expandedDocId, setExpandedDocId] = useState<number | null>(null)
  const [editingDocId, setEditingDocId] = useState<number | null>(null)
  const [editDocTitle, setEditDocTitle] = useState('')
  const [editDocSections, setEditDocSections] = useState<TrialDocumentSection[]>([])
  const [newDocType, setNewDocType] = useState<string | null>(null)
  const [newDocTitle, setNewDocTitle] = useState('')
  const [newDocVersion, setNewDocVersion] = useState('')
  const [newDocSections, setNewDocSections] = useState<TrialDocumentSection[]>([{ heading: '', body: [''] }])
  const [editName, setEditName] = useState('')
  const [editRole, setEditRole] = useState('viewer')
  const [editExpiresAt, setEditExpiresAt] = useState('')
  const [editTerminateAt, setEditTerminateAt] = useState('')
  const [editTerminationReason, setEditTerminationReason] = useState('')
  const [editIsActive, setEditIsActive] = useState(true)
  const [editRequireTotp, setEditRequireTotp] = useState(true)
  const [editRequireAgreement, setEditRequireAgreement] = useState(true)
  const [editRequireNda, setEditRequireNda] = useState(true)
  const [editModuleAccess, setEditModuleAccess] = useState<string[] | null>(null)
  const [resettingPassword, setResettingPassword] = useState(false)
  const [issuedPassword, setIssuedPassword] = useState<TrialPasswordReset | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string | null>(null)
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false)

  const loadLicenses = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<TrialLicense[]>('/admin/trial-licenses')
      setLicenses(res.data)
      setSelectedId((current) => current && res.data.some((item) => item.id === current) ? current : res.data[0]?.id ?? null)
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setError(detail || 'Could not load trial licenses. Confirm the API is reachable and your account has admin access.')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadDocuments = useCallback(async () => {
    try {
      const res = await api.get<TrialDocument[]>('/admin/trial-documents')
      setDocuments(res.data)
    } catch {
      // Non-critical — documents panel will just be empty
    }
  }, [])

  useEffect(() => {
    void loadLicenses()
    void loadDocuments()
  }, [loadLicenses, loadDocuments])

  const selectedLicense = licenses.find((license) => license.id === selectedId) || null

  useEffect(() => {
    if (!selectedLicense) return
    setEditName(selectedLicense.name)
    setEditRole(selectedLicense.role)
    setEditExpiresAt(isoToLocalInput(selectedLicense.trial_expires_at))
    setEditTerminateAt(isoToLocalInput(selectedLicense.trial_termination_scheduled_at))
    setEditTerminationReason(selectedLicense.trial_termination_reason || '')
    setEditIsActive(selectedLicense.is_active)
    setEditRequireTotp(selectedLicense.totp_enabled)
    setEditRequireAgreement(selectedLicense.require_trial_agreement)
    setEditRequireNda(selectedLicense.require_nda)
    setEditModuleAccess(selectedLicense.module_access ?? null)
    // A password belongs to the license it was issued for; switching rows must
    // not leave the previous holder's credential on screen.
    setIssuedPassword((current) => (current && current.id === selectedLicense.id ? current : null))
  }, [selectedLicense])

  const filteredLicenses = licenses.filter((license) => {
    const haystack = `${license.name} ${license.email} ${license.role} ${license.status}`.toLowerCase()
    return haystack.includes(searchTerm.toLowerCase())
  })

  const statusCounts = {
    active: licenses.filter((license) => license.status === 'active').length,
    scheduled: licenses.filter((license) => license.status === 'scheduled').length,
    pendingAgreement: licenses.filter((license) => license.trial_agreement_required || license.nda_required).length,
    terminated: licenses.filter((license) => ['terminated', 'expired'].includes(license.status)).length,
  }

  const formatDate = (value: string | null | undefined) => value ? new Date(value).toLocaleString() : '—'

  const clearFlash = () => {
    setError(null)
    setSuccess(null)
  }

  const handleCreate = useCallback(async () => {
    clearFlash()
    setCreating(true)
    try {
      const res = await api.post<CreatedTrialLicense[]>('/admin/trial-licenses', {
        users: [{
          name: createName,
          email: createEmail,
          role: createRole,
          expires_in_hours: Number(createHours),
          require_totp: createRequireTotp,
          require_trial_agreement: createRequireAgreement,
          require_nda: createRequireNda,
          module_access: createModuleAccess,
        }],
      })
      setCreatedLicenses(res.data)
      const failedDeliveries = res.data.filter((license) => !license.email_sent)
      setCreateName('')
      setCreateEmail('')
      setCreateRole('analyst')
      setCreateHours('168')
      setCreateRequireTotp(true)
      setCreateRequireAgreement(true)
      setCreateRequireNda(true)
      setCreateModuleAccess(null)
      if (failedDeliveries.length > 0) {
        setError(
          `Trial license created, but welcome email failed for ${failedDeliveries.map((license) => license.email).join(', ')}. Check SMTP settings and API logs.`,
        )
      } else {
        setSuccess('Trial license created and welcome email sent. Copy the credentials below before closing this page.')
      }
      await loadLicenses()
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setError(detail || 'Trial license creation failed.')
    } finally {
      setCreating(false)
    }
  }, [createEmail, createHours, createModuleAccess, createName, createRequireTotp, createRequireAgreement, createRequireNda, createRole, loadLicenses])

  const handleSaveSelected = useCallback(async () => {
    if (!selectedLicense) return
    clearFlash()
    setSaving(true)
    try {
      // Only a date the operator changed is sent (null = no change). Re-sending
      // an unchanged one refused every save on a lapsed license ("must be in
      // the future") and, before the local-time fix, moved it by the offset.
      const expiryChanged = editExpiresAt !== isoToLocalInput(selectedLicense.trial_expires_at)
      const scheduleChanged = editTerminateAt !== isoToLocalInput(selectedLicense.trial_termination_scheduled_at)
      const res = await api.patch<TrialLicense>(`/admin/trial-licenses/${selectedLicense.id}`, {
        name: editName,
        role: editRole,
        trial_expires_at: expiryChanged ? localInputToIso(editExpiresAt) : null,
        trial_termination_scheduled_at: scheduleChanged ? localInputToIso(editTerminateAt) : null,
        is_active: editIsActive,
        termination_reason: editTerminationReason || null,
        require_totp: editRequireTotp,
        require_trial_agreement: editRequireAgreement,
        require_nda: editRequireNda,
        // null must reach the server as "go back to unrestricted", not as
        // "leave it alone" — the two are different states and PATCH treats an
        // omitted module_access as no change.
        module_access: editModuleAccess,
        clear_module_access: editModuleAccess === null,
      })
      // Report what the holder can actually do, not that a row was written: a
      // terminated license with a new expiry is still a lockout (C29).
      const saved = res?.data
      // Reload first: loadLicenses clears the error line as it starts.
      await loadLicenses()
      if (saved && saved.can_sign_in === false) {
        setError(
          `Saved, but ${saved.email} still cannot sign in — ${saved.sign_in_blocked_reason || 'access is blocked'}.` +
            (saved.trial_terminated_at ? ' Use Reinstate access to restore it with the expiry above.' : ''),
        )
      } else {
        setSuccess(
          saved?.trial_expires_at
            ? `Trial license updated — ${saved.email} can sign in until ${formatDate(saved.trial_expires_at)}.`
            : 'Trial license updated.',
        )
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setError(detail || 'Update failed.')
    } finally {
      setSaving(false)
    }
  }, [editExpiresAt, editIsActive, editModuleAccess, editName, editRequireAgreement, editRequireNda, editRequireTotp, editRole, editTerminateAt, editTerminationReason, loadLicenses, selectedLicense])

  // Restores a lapsed or terminated license on the SAME account, so its
  // password, authenticator and NDA/agreement acceptance stay valid. Delete and
  // recreate issued a new id and made the holder start over on all three.
  const handleReinstate = useCallback(async () => {
    if (!selectedLicense) return
    clearFlash()
    const expiresAt = localInputToIso(editExpiresAt)
    if (!expiresAt || new Date(expiresAt).getTime() <= Date.now()) {
      setError('Set a Trial Expires date in the future, then reinstate.')
      return
    }
    setSaving(true)
    try {
      const res = await api.post<TrialLicense>(`/admin/trial-licenses/${selectedLicense.id}/reinstate`, {
        trial_expires_at: expiresAt,
      })
      const restored = res.data
      await loadLicenses()
      if (restored.can_sign_in === false) {
        setError(
          `Reinstated, but ${restored.email} still cannot sign in — ${restored.sign_in_blocked_reason || 'access is blocked'}.`,
        )
      } else {
        setSuccess(
          `Access restored for ${restored.email} until ${formatDate(restored.trial_expires_at)} — same password, authenticator and signed agreements.`,
        )
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setError(detail || 'Reinstate failed.')
    } finally {
      setSaving(false)
    }
  }, [editExpiresAt, loadLicenses, selectedLicense])

  const handleResetPassword = useCallback(async (license: TrialLicense, password?: string) => {
    clearFlash()
    setResettingPassword(true)
    try {
      const res = await api.post<TrialPasswordReset>(
        `/admin/trial-licenses/${license.id}/reset-password`,
        password ? { password } : {},
      )
      setIssuedPassword(res.data)
      setSuccess(
        `Password ${res.data.generated ? 'generated' : 'set'} for ${license.email}. Copy it now — it is shown once.`,
      )
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setError(detail || 'Password reset failed.')
    } finally {
      setResettingPassword(false)
    }
  }, [])

  const handleUnschedule = useCallback(async () => {
    if (!selectedLicense) return
    clearFlash()
    setSaving(true)
    try {
      await api.post(`/admin/trial-licenses/${selectedLicense.id}/unschedule-termination`)
      setSuccess('Termination schedule cleared.')
      await loadLicenses()
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setError(detail || 'Could not clear the termination schedule.')
    } finally {
      setSaving(false)
    }
  }, [loadLicenses, selectedLicense])

  const handleTerminate = useCallback(async (license: TrialLicense) => {
    clearFlash()
    setSaving(true)
    try {
      await api.post(`/admin/trial-licenses/${license.id}/terminate`, {
        reason: editTerminationReason || 'manual_termination',
      })
      setSuccess(`Trial license terminated for ${license.email}.`)
      await loadLicenses()
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setError(detail || 'Immediate termination failed.')
    } finally {
      setSaving(false)
    }
  }, [editTerminationReason, loadLicenses])

  const handleDeleteSelected = useCallback(async () => {
    if (!selectedLicense) return
    const confirmed = window.confirm(
      `Delete trial license for ${selectedLicense.email}? This permanently removes the user from the system.`,
    )
    if (!confirmed) return

    clearFlash()
    setSaving(true)
    try {
      await api.delete(`/admin/trial-licenses/${selectedLicense.id}`)
      setSuccess(`Trial license deleted for ${selectedLicense.email}.`)
      await loadLicenses()
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setError(detail || 'Could not delete the trial license.')
    } finally {
      setSaving(false)
    }
  }, [loadLicenses, selectedLicense])

  const handleEmailPreview = useCallback(async () => {
    if (!createName || !createEmail) {
      setError('Enter a name and email to preview the welcome email.')
      return
    }
    setEmailPreviewLoading(true)
    setEmailPreviewHtml(null)
    try {
      const res = await api.post<{ html: string }>('/admin/trial-licenses/preview-email', {
        name: createName,
        email: createEmail,
        expires_in_hours: Number(createHours) || 168,
        require_totp: createRequireTotp,
      })
      setEmailPreviewHtml(res.data.html)
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      setError(detail || 'Could not generate email preview.')
    } finally {
      setEmailPreviewLoading(false)
    }
  }, [createEmail, createHours, createName, createRequireTotp])

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setSuccess(`${label} copied to clipboard.`)
    } catch {
      setError(`Could not copy ${label.toLowerCase()}.`)
    }
  }

  return (
    <div className="space-y-6">
      {error ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
          <AlertCircle size={14} /> {error}
        </div>
      ) : null}
      {success ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-300">
          <CheckCircle2 size={14} /> {success}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: 'Active', value: statusCounts.active },
          { label: 'Scheduled', value: statusCounts.scheduled },
          { label: 'Pending Agreement', value: statusCounts.pendingAgreement },
          { label: 'Terminated / Expired', value: statusCounts.terminated },
        ].map((metric) => (
          <div key={metric.label} className="metric-cell">
            <div className="metric-cell-label">{metric.label}</div>
            <div className="metric-cell-value">{metric.value}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.82fr_1.18fr]">
        <div className="space-y-6">
          <div className="rounded-lg border border-amber-400/10 bg-black/30 p-4">
            <div className="mb-4 flex items-center justify-between">
              <SectionHeading>Create Trial License</SectionHeading>
              <button className="btn-secondary flex items-center gap-1.5 text-[11px]" onClick={() => void loadLicenses()} type="button">
                <RefreshCw size={11} /> Refresh
              </button>
            </div>
            <div className="grid gap-3">
              <div>
                <FormLabel>Trial User Name</FormLabel>
                <FormInput value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="Antarctica evaluator name" />
              </div>
              <div>
                <FormLabel>Email</FormLabel>
                <FormInput value={createEmail} onChange={(e) => setCreateEmail(e.target.value)} placeholder="trial.user@client.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FormLabel>Role</FormLabel>
                  <FormSelect options={['analyst', 'viewer']} value={createRole} onChange={(e) => setCreateRole(e.target.value)} />
                </div>
                <div>
                  <FormLabel>Access Window (hours)</FormLabel>
                  <FormInput type="number" value={createHours} onChange={(e) => setCreateHours(e.target.value)} />
                </div>
              </div>
              <label className="flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2 text-[11px] text-stone-300">
                <input checked={createRequireTotp} className="h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400" onChange={(e) => setCreateRequireTotp(e.target.checked)} type="checkbox" />
                Require authenticator-based 2FA for this trial license
              </label>
              <label className="flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2 text-[11px] text-stone-300">
                <input checked={createRequireAgreement} className="h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400" onChange={(e) => setCreateRequireAgreement(e.target.checked)} type="checkbox" />
                Require Trial Agreement acceptance
              </label>
              <label className="flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2 text-[11px] text-stone-300">
                <input checked={createRequireNda} className="h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400" onChange={(e) => setCreateRequireNda(e.target.checked)} type="checkbox" />
                Require NDA acceptance
              </label>
              <ModuleAccessEditor
                disabled={creating}
                onChange={setCreateModuleAccess}
                // Nothing is issued yet, so nothing can be role-blocked to
                // flag; the editor's role warnings appear once the license
                // exists and the server has resolved them.
                visibleModules={createModuleAccess ?? []}
                value={createModuleAccess}
              />
              <div className="flex gap-2">
                <button
                  className="btn-primary flex flex-1 items-center justify-center gap-2 text-[12px]"
                  disabled={creating || !createName || !createEmail || !createHours}
                  onClick={() => void handleCreate()}
                  type="button"
                >
                  <Plus size={12} /> {creating ? 'Creating…' : 'Create Trial License'}
                </button>
                <button
                  className="btn-secondary flex items-center justify-center gap-2 text-[12px]"
                  disabled={emailPreviewLoading || !createName || !createEmail}
                  onClick={() => void handleEmailPreview()}
                  type="button"
                >
                  <Mail size={12} /> {emailPreviewLoading ? 'Loading…' : 'Preview Email'}
                </button>
                <button
                  className="btn-secondary flex items-center justify-center gap-2 text-[12px]"
                  onClick={() => setShowPreview(true)}
                  type="button"
                >
                  <EyeIcon size={12} /> Preview UX
                </button>
              </div>
            </div>
          </div>

          {createdLicenses.length > 0 ? (
            <div className="rounded-lg border border-amber-400/10 bg-black/30 p-4">
              <SectionHeading>Generated Credentials</SectionHeading>
              <div className="space-y-3">
                {createdLicenses.map((license) => (
                  <div key={license.id} className="rounded-lg border border-amber-400/10 bg-black/20 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[12px] font-medium text-stone-100">{license.name}</div>
                        <div className="text-[11px] text-stone-500">{license.email}</div>
                      </div>
                      <StatusBadge status="Active" />
                    </div>
                    <div className="mt-3 grid gap-2">
                      <div className={`rounded-md border px-3 py-2 text-[10px] ${license.email_sent ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300' : 'border-red-500/25 bg-red-500/10 text-red-300'}`}>
                        {license.email_sent
                          ? 'Welcome email sent successfully.'
                          : (license.email_error || 'Welcome email could not be delivered.')}
                      </div>
                      <div className="rounded-md border border-amber-400/10 bg-black/25 px-3 py-2">
                        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">Password</div>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <span className="font-mono text-[11px] text-amber-200">{license.password}</span>
                          <button className="text-[10px] text-amber-300 hover:text-amber-200" onClick={() => void copyToClipboard(license.password, 'Password')} type="button">
                            <Copy size={10} className="inline mr-1" /> Copy
                          </button>
                        </div>
                      </div>
                      {license.totp_secret ? (
                        <div className="rounded-md border border-amber-400/10 bg-black/25 px-3 py-2">
                          <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">TOTP Secret</div>
                          <div className="mt-1 flex items-center justify-between gap-2">
                            <span className="font-mono text-[11px] text-stone-300">{license.totp_secret}</span>
                            <button className="text-[10px] text-amber-300 hover:text-amber-200" onClick={() => void copyToClipboard(license.totp_secret || '', 'TOTP secret')} type="button">
                              <Copy size={10} className="inline mr-1" /> Copy
                            </button>
                          </div>
                        </div>
                      ) : null}
                      <div className="text-[10px] text-stone-500">Access expires {formatDate(license.expires_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Trial Documents Management */}
          <div className="rounded-lg border border-amber-400/10 bg-black/30 p-4">
            <div className="mb-4 flex items-center justify-between">
              <SectionHeading>Trial Documents</SectionHeading>
              <button className="btn-secondary flex items-center gap-1.5 text-[11px]" onClick={() => void loadDocuments()} type="button">
                <RefreshCw size={11} /> Refresh
              </button>
            </div>

            {(['agreement', 'nda'] as const).map((docType) => {
              const activeDoc = documents.find((d) => d.document_type === docType && d.is_active)
              const allVersions = documents.filter((d) => d.document_type === docType)
              const isExpanded = activeDoc && expandedDocId === activeDoc.id
              const isEditing = activeDoc && editingDocId === activeDoc.id
              const isCreatingNew = newDocType === docType

              return (
                <div key={docType} className="mb-4 rounded-lg border border-amber-400/10 bg-black/20 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[12px] font-medium text-stone-200">
                        {docType === 'agreement' ? 'Trial Agreement' : 'Non-Disclosure Agreement (NDA)'}
                      </div>
                      {activeDoc ? (
                        <div className="text-[10px] text-stone-500">
                          {activeDoc.title} &middot; v{activeDoc.version} &middot; {allVersions.length} version{allVersions.length !== 1 ? 's' : ''}
                        </div>
                      ) : (
                        <div className="text-[10px] text-stone-500">No active document</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {activeDoc ? (
                        <>
                          <button
                            className="text-[10px] text-amber-300 hover:text-amber-200"
                            onClick={() => setExpandedDocId(isExpanded ? null : activeDoc.id)}
                            type="button"
                          >
                            <ChevronDown size={12} className={`inline mr-0.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            {isExpanded ? 'Collapse' : 'View'}
                          </button>
                          <button
                            className="text-[10px] text-amber-300 hover:text-amber-200"
                            onClick={() => {
                              if (isEditing) {
                                setEditingDocId(null)
                              } else {
                                setEditingDocId(activeDoc.id)
                                setEditDocTitle(activeDoc.title)
                                setEditDocSections([...activeDoc.sections])
                                setExpandedDocId(activeDoc.id)
                              }
                            }}
                            type="button"
                          >
                            {isEditing ? 'Cancel Edit' : 'Edit'}
                          </button>
                        </>
                      ) : null}
                      <button
                        className="text-[10px] text-amber-300 hover:text-amber-200"
                        onClick={() => {
                          if (isCreatingNew) {
                            setNewDocType(null)
                          } else {
                            setNewDocType(docType)
                            setNewDocTitle(activeDoc?.title || (docType === 'agreement' ? 'Trial Agreement' : 'Non-Disclosure Agreement'))
                            setNewDocVersion('')
                            setNewDocSections(activeDoc ? [...activeDoc.sections.map((s) => ({ ...s, body: [...s.body] }))] : [{ heading: '', body: [''] }])
                          }
                        }}
                        type="button"
                      >
                        <Plus size={10} className="inline mr-0.5" /> New Version
                      </button>
                    </div>
                  </div>

                  {/* View document sections */}
                  {isExpanded && activeDoc && !isEditing ? (
                    <div className="mt-3 max-h-[300px] overflow-y-auto rounded-md border border-amber-400/10 bg-black/25 p-3">
                      {activeDoc.sections.map((section, i) => (
                        <div key={i} className="mb-3">
                          <div className="text-[11px] font-semibold text-stone-200">{section.heading}</div>
                          <ul className="ml-3 mt-1 space-y-1">
                            {section.body.map((bullet, j) => (
                              <li key={j} className="text-[10px] text-stone-400 leading-relaxed">- {bullet}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* Edit existing document */}
                  {isEditing && activeDoc ? (
                    <div className="mt-3 space-y-3">
                      <div>
                        <FormLabel>Title</FormLabel>
                        <FormInput value={editDocTitle} onChange={(e) => setEditDocTitle(e.target.value)} />
                      </div>
                      {editDocSections.map((section, si) => (
                        <div key={si} className="rounded-md border border-amber-400/10 bg-black/20 p-2">
                          <div className="flex items-center gap-2">
                            <FormInput
                              value={section.heading}
                              onChange={(e) => {
                                const updated = [...editDocSections]
                                updated[si] = { ...updated[si], heading: e.target.value }
                                setEditDocSections(updated)
                              }}
                              placeholder="Section heading"
                            />
                            <button className="text-[10px] text-red-400 hover:text-red-300 shrink-0" onClick={() => setEditDocSections(editDocSections.filter((_, idx) => idx !== si))} type="button">Remove</button>
                          </div>
                          {section.body.map((bullet, bi) => (
                            <div key={bi} className="mt-1 flex items-start gap-1">
                              <span className="text-[10px] text-stone-600 mt-2">-</span>
                              <textarea
                                className="w-full resize-none rounded border border-amber-400/10 bg-black/30 px-2 py-1.5 text-[10px] text-stone-300 outline-none focus:border-amber-400/30"
                                rows={2}
                                value={bullet}
                                onChange={(e) => {
                                  const updated = [...editDocSections]
                                  const newBody = [...updated[si].body]
                                  newBody[bi] = e.target.value
                                  updated[si] = { ...updated[si], body: newBody }
                                  setEditDocSections(updated)
                                }}
                              />
                              <button className="text-[10px] text-red-400 hover:text-red-300 mt-1.5 shrink-0" onClick={() => {
                                const updated = [...editDocSections]
                                updated[si] = { ...updated[si], body: updated[si].body.filter((_, idx) => idx !== bi) }
                                setEditDocSections(updated)
                              }} type="button">x</button>
                            </div>
                          ))}
                          <button className="mt-1 text-[10px] text-amber-300 hover:text-amber-200" onClick={() => {
                            const updated = [...editDocSections]
                            updated[si] = { ...updated[si], body: [...updated[si].body, ''] }
                            setEditDocSections(updated)
                          }} type="button">+ Add bullet</button>
                        </div>
                      ))}
                      <button className="text-[10px] text-amber-300 hover:text-amber-200" onClick={() => setEditDocSections([...editDocSections, { heading: '', body: [''] }])} type="button">
                        <Plus size={10} className="inline mr-0.5" /> Add Section
                      </button>
                      <div className="flex gap-2">
                        <button
                          className="btn-primary text-[11px]"
                          onClick={async () => {
                            clearFlash()
                            try {
                              await api.patch(`/admin/trial-documents/${activeDoc.id}`, {
                                title: editDocTitle,
                                sections: editDocSections,
                              })
                              setSuccess('Document updated.')
                              setEditingDocId(null)
                              await loadDocuments()
                            } catch {
                              setError('Failed to update document.')
                            }
                          }}
                          type="button"
                        >
                          Save Changes
                        </button>
                        <button className="btn-secondary text-[11px]" onClick={() => setEditingDocId(null)} type="button">Cancel</button>
                      </div>
                    </div>
                  ) : null}

                  {/* Create new version */}
                  {isCreatingNew ? (
                    <div className="mt-3 space-y-3 rounded-md border border-amber-400/10 bg-black/25 p-3">
                      <div className="text-[11px] font-medium text-stone-200">New Version</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <FormLabel>Title</FormLabel>
                          <FormInput value={newDocTitle} onChange={(e) => setNewDocTitle(e.target.value)} />
                        </div>
                        <div>
                          <FormLabel>Version</FormLabel>
                          <FormInput value={newDocVersion} onChange={(e) => setNewDocVersion(e.target.value)} placeholder="e.g. 2026-03-21-v2" />
                        </div>
                      </div>
                      {newDocSections.map((section, si) => (
                        <div key={si} className="rounded-md border border-amber-400/10 bg-black/20 p-2">
                          <div className="flex items-center gap-2">
                            <FormInput
                              value={section.heading}
                              onChange={(e) => {
                                const updated = [...newDocSections]
                                updated[si] = { ...updated[si], heading: e.target.value }
                                setNewDocSections(updated)
                              }}
                              placeholder="Section heading"
                            />
                            <button className="text-[10px] text-red-400 hover:text-red-300 shrink-0" onClick={() => setNewDocSections(newDocSections.filter((_, idx) => idx !== si))} type="button">Remove</button>
                          </div>
                          {section.body.map((bullet, bi) => (
                            <div key={bi} className="mt-1 flex items-start gap-1">
                              <span className="text-[10px] text-stone-600 mt-2">-</span>
                              <textarea
                                className="w-full resize-none rounded border border-amber-400/10 bg-black/30 px-2 py-1.5 text-[10px] text-stone-300 outline-none focus:border-amber-400/30"
                                rows={2}
                                value={bullet}
                                onChange={(e) => {
                                  const updated = [...newDocSections]
                                  const newBody = [...updated[si].body]
                                  newBody[bi] = e.target.value
                                  updated[si] = { ...updated[si], body: newBody }
                                  setNewDocSections(updated)
                                }}
                              />
                              <button className="text-[10px] text-red-400 hover:text-red-300 mt-1.5 shrink-0" onClick={() => {
                                const updated = [...newDocSections]
                                updated[si] = { ...updated[si], body: updated[si].body.filter((_, idx) => idx !== bi) }
                                setNewDocSections(updated)
                              }} type="button">x</button>
                            </div>
                          ))}
                          <button className="mt-1 text-[10px] text-amber-300 hover:text-amber-200" onClick={() => {
                            const updated = [...newDocSections]
                            updated[si] = { ...updated[si], body: [...updated[si].body, ''] }
                            setNewDocSections(updated)
                          }} type="button">+ Add bullet</button>
                        </div>
                      ))}
                      <button className="text-[10px] text-amber-300 hover:text-amber-200" onClick={() => setNewDocSections([...newDocSections, { heading: '', body: [''] }])} type="button">
                        <Plus size={10} className="inline mr-0.5" /> Add Section
                      </button>
                      <div className="flex gap-2">
                        <button
                          className="btn-primary text-[11px]"
                          disabled={!newDocTitle || !newDocVersion || newDocSections.length === 0}
                          onClick={async () => {
                            clearFlash()
                            try {
                              await api.post('/admin/trial-documents', {
                                document_type: docType,
                                title: newDocTitle,
                                version: newDocVersion,
                                sections: newDocSections,
                                is_active: true,
                              })
                              setSuccess(`New ${docType === 'agreement' ? 'agreement' : 'NDA'} version created and set as active.`)
                              setNewDocType(null)
                              await loadDocuments()
                            } catch (e: unknown) {
                              const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
                              setError(detail || 'Failed to create new document version.')
                            }
                          }}
                          type="button"
                        >
                          Create &amp; Activate
                        </button>
                        <button className="btn-secondary text-[11px]" onClick={() => setNewDocType(null)} type="button">Cancel</button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/40 px-3 py-1.5">
            <Search size={13} className="text-stone-600" />
            <input
              type="text"
              placeholder="Search trial licenses by name, email, role, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent text-[12px] text-stone-300 placeholder:text-stone-600 outline-none"
            />
          </div>

          <div className="rounded-lg border border-amber-400/10 bg-black/30 overflow-hidden">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Agreement</th>
                  <th>NDA</th>
                  <th>Modules</th>
                  <th>Expires</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-[12px] text-stone-500">Loading trial licenses…</td>
                  </tr>
                ) : filteredLicenses.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-[12px] text-stone-500">No trial licenses matched your search.</td>
                  </tr>
                ) : filteredLicenses.map((license) => (
                  <tr
                    key={license.id}
                    className={`cursor-pointer ${selectedId === license.id ? 'bg-amber-400/[0.05]' : ''}`}
                    onClick={() => setSelectedId(license.id)}
                  >
                    <td className="font-medium text-stone-200">{license.name}</td>
                    <td className="text-[11px] text-stone-400">{license.email}</td>
                    <td><StatusBadge status={license.status} /></td>
                    <td>
                      {license.require_trial_agreement ? (
                        <StatusBadge status={license.trial_agreement_required ? 'Pending' : 'Accepted'} />
                      ) : (
                        <span className="text-[10px] text-stone-600">N/A</span>
                      )}
                    </td>
                    <td>
                      {license.require_nda ? (
                        <StatusBadge status={license.nda_required ? 'Pending' : 'Accepted'} />
                      ) : (
                        <span className="text-[10px] text-stone-600">N/A</span>
                      )}
                    </td>
                    <td>
                      {license.module_access === null ? (
                        <span className="text-[10px] text-stone-500">All</span>
                      ) : (
                        <span
                          className={`text-[10px] font-medium ${license.visible_modules.length === 0 ? 'text-red-400' : 'text-amber-300'}`}
                          title={license.visible_modules.map((id) => getWorkflowMeta(id as PlatformModuleId)?.label ?? id).join(', ') || 'No modules'}
                        >
                          {license.visible_modules.length} of {WORKFLOW_ORDER.length}
                        </span>
                      )}
                    </td>
                    <td className="text-[11px] text-stone-400">{formatDate(license.trial_expires_at)}</td>
                    <td className="text-[11px] text-stone-500">{formatDate(license.last_login_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedLicense ? (
            <div className="rounded-lg border border-amber-400/10 bg-black/30 p-4">
              <div className="flex items-center justify-between">
                <SectionHeading>Manage Selected Trial License</SectionHeading>
                <StatusBadge status={selectedLicense.status} />
              </div>
              {selectedLicense.can_sign_in === false ? (
                <p className="mb-2 text-[11px] text-amber-300" role="note">
                  {selectedLicense.email} cannot sign in: {selectedLicense.sign_in_blocked_reason || 'access is blocked'}.
                  {selectedLicense.trial_terminated_at
                    ? ' Set a new Trial Expires date and use Reinstate access — the same account comes back.'
                    : ''}
                </p>
              ) : null}
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <FormLabel>Name</FormLabel>
                  <FormInput value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>
                <div>
                  <FormLabel>Role</FormLabel>
                  <FormSelect options={['viewer', 'analyst']} value={editRole} onChange={(e) => setEditRole(e.target.value)} />
                </div>
                <div>
                  <FormLabel>Trial Expires</FormLabel>
                  <FormInput type="datetime-local" aria-label="Trial Expires" value={editExpiresAt} onChange={(e) => setEditExpiresAt(e.target.value)} />
                </div>
                <div>
                  <FormLabel>Schedule Termination</FormLabel>
                  <FormInput type="datetime-local" aria-label="Schedule Termination" value={editTerminateAt} onChange={(e) => setEditTerminateAt(e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <FormLabel>Termination Reason</FormLabel>
                  <FormInput value={editTerminationReason} onChange={(e) => setEditTerminationReason(e.target.value)} placeholder="e.g. Trial complete, procurement delayed, no longer approved" />
                </div>
              </div>

              <div className="mt-3 space-y-2">
                <TrialTwoFactorToggle checked={editRequireTotp} enrolled={selectedLicense.totp_enabled} onChange={setEditRequireTotp} />
                <label className="flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2 text-[11px] text-stone-300">
                  <input checked={editRequireAgreement} className="h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400" onChange={(e) => setEditRequireAgreement(e.target.checked)} type="checkbox" />
                  Require Trial Agreement acceptance
                  <span className="ml-auto text-[10px]">
                    {selectedLicense.require_trial_agreement
                      ? (selectedLicense.trial_agreement_required ? <span className="text-amber-300">Pending</span> : <span className="text-emerald-400">Accepted {formatDate(selectedLicense.trial_agreement_accepted_at)}</span>)
                      : <span className="text-stone-500">Disabled</span>}
                  </span>
                </label>
                <label className="flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2 text-[11px] text-stone-300">
                  <input checked={editRequireNda} className="h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400" onChange={(e) => setEditRequireNda(e.target.checked)} type="checkbox" />
                  Require NDA acceptance
                  <span className="ml-auto text-[10px]">
                    {selectedLicense.require_nda
                      ? (selectedLicense.nda_required ? <span className="text-amber-300">Pending</span> : <span className="text-emerald-400">Accepted {formatDate(selectedLicense.nda_accepted_at)}</span>)
                      : <span className="text-stone-500">Disabled</span>}
                  </span>
                </label>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="metric-cell">
                  <div className="metric-cell-label">Created</div>
                  <div className="metric-cell-value text-[13px]">{formatDate(selectedLicense.created_at)}</div>
                  <div className="mt-1 text-[10px] text-stone-500">{selectedLicense.trial_created_by || '—'}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="metric-cell">
                  <div className="metric-cell-label">Termination Scheduled</div>
                  <div className="metric-cell-value text-[13px]">{formatDate(selectedLicense.trial_termination_scheduled_at)}</div>
                </div>
                <div className="metric-cell">
                  <div className="metric-cell-label">Terminated</div>
                  <div className="metric-cell-value text-[13px]">{formatDate(selectedLicense.trial_terminated_at)}</div>
                </div>
              </div>

              <div className="mt-3">
                <ModuleAccessEditor
                  disabled={saving}
                  onChange={setEditModuleAccess}
                  value={editModuleAccess}
                  visibleModules={selectedLicense.visible_modules}
                />
                <p className="mt-1.5 text-[10px] text-stone-600">
                  Saved with the license below. A module outside this list is absent from the
                  holder&rsquo;s sidebar and its API routes refuse the request — enforced per
                  request, so removing one revokes it on the holder&rsquo;s next action.
                </p>
              </div>

              <div className="mt-3">
                <TrialPasswordResetPanel
                  busy={resettingPassword}
                  issued={issuedPassword}
                  license={selectedLicense}
                  onCopy={(value, label) => void copyToClipboard(value, label)}
                  onDismiss={() => setIssuedPassword(null)}
                  onSubmit={(password) => void handleResetPassword(selectedLicense, password)}
                />
              </div>

              <label className="mt-4 flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2 text-[11px] text-stone-300">
                <input checked={editIsActive} className="h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400" onChange={(e) => setEditIsActive(e.target.checked)} type="checkbox" />
                License is active and can authenticate until expiry or termination
              </label>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button className="btn-primary text-[12px]" disabled={saving} onClick={() => void handleSaveSelected()} type="button">
                  {saving ? 'Saving…' : 'Save Trial License'}
                </button>
                {selectedLicense.can_sign_in === false ? (
                  <button
                    className="btn-secondary text-[12px]"
                    disabled={saving || !editExpiresAt}
                    onClick={() => void handleReinstate()}
                    title="Restore sign-in on this same account with the Trial Expires date above"
                    type="button"
                  >
                    <RotateCcw size={12} className="inline mr-1" /> Reinstate access
                  </button>
                ) : null}
                <button className="btn-secondary text-[12px]" disabled={saving || !selectedLicense.trial_termination_scheduled_at} onClick={() => void handleUnschedule()} type="button">
                  <RotateCcw size={12} className="inline mr-1" /> Clear Schedule
                </button>
                <button className="text-[12px] text-red-400 hover:text-red-300" disabled={saving || ['terminated', 'expired'].includes(selectedLicense.status)} onClick={() => void handleTerminate(selectedLicense)} type="button">
                  Terminate Now
                </button>
                <button className="text-[12px] text-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:text-red-400/50" disabled={saving} onClick={() => void handleDeleteSelected()} type="button">
                  Delete License
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showPreview ? (
        <TrialExperiencePreview
          userName={createName}
          userEmail={createEmail}
          requireTotp={createRequireTotp}
          requireAgreement={createRequireAgreement}
          requireNda={createRequireNda}
          onClose={() => setShowPreview(false)}
        />
      ) : null}

      {/* Email Preview Dialog */}
      {emailPreviewHtml ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setEmailPreviewHtml(null)}>
          <div
            className="relative flex max-h-[90vh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl border border-amber-400/15 bg-[#0a0a0a] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-shrink-0 items-center justify-between border-b border-amber-400/10 px-5 py-3">
              <div className="flex items-center gap-2.5">
                <Mail size={15} className="text-[#d9b768]" />
                <span className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#d9b768]">Welcome Email Preview</span>
                <span className="rounded-full border border-stone-700 bg-black/50 px-2 py-0.5 text-[10px] text-stone-400">
                  {createName || 'Trial User'} &middot; {createEmail || 'user@example.com'}
                </span>
              </div>
              <button onClick={() => setEmailPreviewHtml(null)} className="rounded-full p-1.5 text-stone-400 transition hover:bg-stone-800 hover:text-white">
                <CheckCircle2 size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <iframe
                srcDoc={emailPreviewHtml}
                title="Email Preview"
                className="h-[70vh] w-full border-0"
                sandbox=""
              />
            </div>
            <div className="flex flex-shrink-0 items-center justify-between border-t border-amber-400/10 bg-black/30 px-5 py-2.5">
              <div className="text-[10px] text-stone-500">This is a preview only — no email has been sent.</div>
              <button className="btn-primary flex items-center gap-1.5 text-[12px]" onClick={() => setEmailPreviewHtml(null)} type="button">
                Close Preview
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ActivityTab() {
  return <AdminActivitySettingsTab />
}

function IntegrationsTabAzure() {
  const { accounts } = useMsal()
  const email = accounts[0]?.username ?? ''
  return <IntegrationsHub sessionEmail={email} />
}

function IntegrationsTab() {
  const { isLocalAuth, localUser } = useLocalAuth()
  if (isLocalAuth) return <IntegrationsHub sessionEmail={localUser?.email ?? ''} />
  if (!isAzureAuthEnabled) return <IntegrationsHub sessionEmail={devSessionAccount.username} />
  return <IntegrationsTabAzure />
}

function DatabaseTab() {
  return <DatabaseSettingsTab />
}


/**
 * Platform-level configuration: retention, deal governance, and identity
 * suppression. API keys, the AI agent, mail collection, and inbound webhooks
 * live on Integrations.
 */
export function PlatformSettingsTab() {
  const [loadError, setLoadError] = useState<string | null>(null)
  const [permError, setPermError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)

  const [platformName, setPlatformName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [notifications, setNotifications] = useState({
    dealStage: true,
    triggerAlerts: true,
    committee: true,
    reports: false,
    system: true,
  })
  const [retentionPeriod, setRetentionPeriod] = useState('7 Years')
  const [templateApprovalMode, setTemplateApprovalMode] = useState<'dual' | 'single'>('dual')
  const [priorityRules, setPriorityRules] = useState<{
    default: string
    rules: { asset_classes: string[] | 'ALL'; min_amount?: number | null; max_amount?: number | null; priority: string }[]
  }>({ default: 'Medium', rules: [] })
  // Raw multiline text (one entry per line) so typing stays natural; parsed
  // into arrays at save time.
  const [idSuppress, setIdSuppress] = useState({
    emailDomains: '',
    officeAddresses: '',
    phoneNumbers: '',
    legalEntityNames: '',
    people: '',
  })
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoadError(null)
      setPermError(null)
      try {
        const plat = await api.get<{ data: Record<string, unknown> }>('/settings/platform')
        if (cancelled) return
        const d = plat.data?.data ?? {}
        setPlatformName(String(d.platformName ?? ''))
        setCompanyName(String(d.companyName ?? ''))
        const n = d.notifications
        if (n && typeof n === 'object') {
          setNotifications((prev) => ({
            dealStage: Boolean((n as Record<string, boolean>).dealStage ?? prev.dealStage),
            triggerAlerts: Boolean((n as Record<string, boolean>).triggerAlerts ?? prev.triggerAlerts),
            committee: Boolean((n as Record<string, boolean>).committee ?? prev.committee),
            reports: Boolean((n as Record<string, boolean>).reports ?? prev.reports),
            system: Boolean((n as Record<string, boolean>).system ?? prev.system),
          }))
        }
        setRetentionPeriod(String(d.retentionPeriod ?? '7 Years'))
        setTemplateApprovalMode(d.templateApprovalMode === 'single' ? 'single' : 'dual')
        const pr = d.priorityRules as typeof priorityRules | undefined
        if (pr && typeof pr === 'object') {
          setPriorityRules({
            default: typeof pr.default === 'string' ? pr.default : 'Medium',
            rules: Array.isArray(pr.rules) ? pr.rules : [],
          })
        }
        const sup = d.identitySuppression as Record<string, unknown> | undefined
        if (sup && typeof sup === 'object') {
          const lines = (v: unknown) =>
            Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string').join('\n') : ''
          setIdSuppress({
            emailDomains: lines(sup.emailDomains),
            officeAddresses: lines(sup.officeAddresses),
            phoneNumbers: lines(sup.phoneNumbers),
            legalEntityNames: lines(sup.legalEntityNames),
            people: lines(sup.people),
          })
        }
      } catch (e: unknown) {
        if (cancelled) return
        const status = (e as { response?: { status?: number } }).response?.status
        if (status === 403) setPermError('You need an admin role to edit platform settings. You can still view this page.')
        else setLoadError('Could not load platform settings. Check that the API is reachable.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setSaveOk(false)
    setLoadError(null)
    setPermError(null)
    try {
      const toList = (raw: string) =>
        raw.split('\n').map((s) => s.trim()).filter(Boolean)
      const payload: Record<string, unknown> = {
          platformName,
          companyName,
          notifications,
          retentionPeriod,
          templateApprovalMode,
          priorityRules,
          identitySuppression: {
            emailDomains: toList(idSuppress.emailDomains),
            officeAddresses: toList(idSuppress.officeAddresses),
            phoneNumbers: toList(idSuppress.phoneNumbers),
            legalEntityNames: toList(idSuppress.legalEntityNames),
            people: toList(idSuppress.people),
          },
      }
      await api.patch('/settings/platform', { data: payload })
      setSaveOk(true)
      setTimeout(() => setSaveOk(false), 4000)
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number; data?: { detail?: string } } }).response?.status
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
      if (status === 403) setPermError(typeof detail === 'string' ? detail : 'Admin role required.')
      else setLoadError('Save failed. Check your connection and permissions.')
    } finally {
      setSaving(false)
    }
  }, [
    platformName,
    companyName,
    notifications,
    retentionPeriod,
    templateApprovalMode,
    priorityRules,
    idSuppress,
  ])

  return (
    <div className="space-y-6">
      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
          <AlertCircle size={14} /> {loadError}
        </div>
      )}
      {permError && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">
          <AlertCircle size={14} /> {permError}
        </div>
      )}
      {loading ? <div className="text-[12px] text-stone-500">Loading platform settings…</div> : null}

      <SectionHeading>Data Retention</SectionHeading>
      <div className="max-w-xs">
        <FormLabel>Retention Period</FormLabel>
        <FormSelect
          options={['1 Year', '3 Years', '5 Years', '7 Years', 'Indefinite']}
          value={retentionPeriod}
          onChange={(e) => setRetentionPeriod(e.target.value)}
        />
      </div>

      <div className="h-px bg-amber-400/10" />

      <SectionHeading>Deal Governance</SectionHeading>
      <div className="max-w-lg space-y-4">
        <div className="max-w-xs">
          <FormLabel>Model-Template Approval</FormLabel>
          <FormSelect
            options={['Dual approval (four-eyes)', 'Single user']}
            value={templateApprovalMode === 'single' ? 'Single user' : 'Dual approval (four-eyes)'}
            onChange={(e) =>
              setTemplateApprovalMode(e.target.value === 'Single user' ? 'single' : 'dual')
            }
          />
          <p className="mt-1 text-[10px] text-stone-500">
            Single-user mode lets the author approve their own template version; the mode in
            force is recorded in the tamper-evident audit chain on every approval.
          </p>
          <p className="mt-1 text-[10px]">
            {/* The chain itself — submit, approve, lock — and the onboarding drafts (UI-04). */}
            <a href="/model-templates" className="text-amber-300 underline decoration-dotted underline-offset-2 hover:text-amber-200">
              Open model template governance
            </a>
          </p>
        </div>

        <div>
          <FormLabel>Intake Priority Rules</FormLabel>
          <p className="mb-2 text-[10px] text-stone-500">
            First matching rule assigns the priority; nothing matches → the default.
            Asset classes are the engine's enums (e.g. MULTIFAMILY, INDUSTRIAL, OFFICE) or ALL;
            amounts are absolute dollars, bounds inclusive.
          </p>
          <div className="space-y-1.5">
            {priorityRules.rules.map((rule, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <input
                  value={rule.asset_classes === 'ALL' ? 'ALL' : (rule.asset_classes ?? []).join(', ')}
                  onChange={(e) => {
                    const raw = e.target.value.trim()
                    const parsed = raw.toUpperCase() === 'ALL'
                      ? ('ALL' as const)
                      : raw.split(',').map((t) => t.trim().toUpperCase().replace(/\s+/g, '_')).filter(Boolean)
                    setPriorityRules((p) => ({
                      ...p,
                      rules: p.rules.map((r, j) => (j === i ? { ...r, asset_classes: parsed } : r)),
                    }))
                  }}
                  placeholder="MULTIFAMILY, INDUSTRIAL or ALL"
                  className="input flex-1 text-[11px] font-mono"
                />
                <input
                  type="number"
                  value={rule.min_amount ?? ''}
                  onChange={(e) => setPriorityRules((p) => ({
                    ...p,
                    rules: p.rules.map((r, j) => (j === i ? { ...r, min_amount: e.target.value === '' ? null : Number(e.target.value) } : r)),
                  }))}
                  placeholder="min $"
                  className="input w-[110px] text-[11px]"
                />
                <input
                  type="number"
                  value={rule.max_amount ?? ''}
                  onChange={(e) => setPriorityRules((p) => ({
                    ...p,
                    rules: p.rules.map((r, j) => (j === i ? { ...r, max_amount: e.target.value === '' ? null : Number(e.target.value) } : r)),
                  }))}
                  placeholder="max $"
                  className="input w-[110px] text-[11px]"
                />
                <FormSelect
                  options={['High', 'Medium', 'Low']}
                  value={rule.priority}
                  onChange={(e) => setPriorityRules((p) => ({
                    ...p,
                    rules: p.rules.map((r, j) => (j === i ? { ...r, priority: e.target.value } : r)),
                  }))}
                />
                <button
                  type="button"
                  onClick={() => setPriorityRules((p) => ({
                    ...p, rules: p.rules.filter((_, j) => j !== i),
                  }))}
                  className="px-2 py-1 text-[11px] text-stone-500 hover:text-red-300"
                  title="Remove rule"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPriorityRules((p) => ({
                ...p,
                rules: [...p.rules, { asset_classes: 'ALL', min_amount: null, max_amount: null, priority: 'High' }],
              }))}
              className="btn-secondary text-[10px] px-3 py-1.5"
            >
              Add rule
            </button>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-stone-500">Default priority</span>
              <FormSelect
                options={['High', 'Medium', 'Low']}
                value={priorityRules.default}
                onChange={(e) => setPriorityRules((p) => ({ ...p, default: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div>
          <FormLabel>Tenant Identity Suppression</FormLabel>
          <p className="mb-2 text-[10px] text-stone-500">
            Your firm's own identity, filtered out of intake extraction so a signature
            block never becomes deal data — the office address is never read as the
            subject property, your entity is never read as the sponsor, and an internal
            forwarder is never recorded as the broker. One entry per line. Your company
            and client-profile names are suppressed automatically.
          </p>
          {!loading && !idSuppress.emailDomains.trim() && !idSuppress.people.trim() ? (
            <p
              role="note"
              aria-label="Identity suppression not configured"
              className="mb-2 rounded border border-amber-400/30 bg-amber-400/[0.08] px-2 py-1.5 text-[11px] text-amber-200"
            >
              Not configured: no email domains and no people are listed, so your own staff and intake mailbox are
              recorded as outside parties — brokers and &ldquo;Other party&rdquo; — on every deal and in the CRM. List
              your firm&apos;s email domains and your people below, save, then use Re-read deals in the CRM.
            </p>
          ) : null}
          <p className="mb-2 text-[10px] text-amber-300/70">
            Include firms you have <span className="font-medium">acquired</span>, and any
            domain your people still send from. Their colleagues arrive in a forwarded
            chain looking exactly like an external broker; listing them here stops that,
            and the person is kept as the deal's Internal Contact rather than dropped.
            List <span className="font-medium">your own people</span> too: a colleague's
            name in a forwarded chain was being written into Brokerage.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {(
              [
                ['emailDomains', 'Email domains', 'yourfirm.com\nacquiredfirm.com'],
                ['officeAddresses', 'Office addresses', '100 Main Street, New York, NY 10001'],
                ['phoneNumbers', 'Phone numbers', '(212) 555-0100'],
                ['legalEntityNames', 'Legal entity names', 'Your Firm Capital Management LLC\nAcquired Firm Capital LLC'],
                ['people', 'Your own people', 'Spencer Kane\nChris Young'],
              ] as const
            ).map(([key, label, placeholder]) => (
              <div key={key}>
                <span className="mb-1 block text-[10px] font-medium text-stone-400">{label}</span>
                <textarea
                  value={idSuppress[key]}
                  onChange={(e) => setIdSuppress((p) => ({ ...p, [key]: e.target.value }))}
                  placeholder={placeholder}
                  rows={3}
                  className="input w-full resize-y text-[11px] font-mono"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="h-px bg-amber-400/10" />

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          type="button"
          className="btn-primary text-[12px] disabled:opacity-50"
          disabled={loading || saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
        {saveOk ? (
          <span className="flex items-center gap-1 text-[11px] text-emerald-400">
            <CheckCircle2 size={12} /> Saved
          </span>
        ) : null}
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

/** Admin-only tabs — only visible to super admins (edward@helios-fintech.com + is_super_admin). */
const ADMIN_ONLY_TABS: Set<TabId> = new Set([
  'permissions',
  'users',
  'trialLicenses',
  'activity',
  'integrations',
  'database',
  'referenceData',
  'demoData',
  'sla',
  'platform',
])

/** Tabs hidden from trial users — they only see Profile and Security. */
const TRIAL_HIDDEN_TABS: Set<TabId> = new Set([
  'permissions',
  'users',
  'trialLicenses',
  'activity',
  'integrations',
  'database',
  'referenceData',
  'demoData',
  'sla',
  'platform',
])

export default function UserProfile() {
  const { localUser } = useLocalAuth()
  const [activeTab, setActiveTab] = useState<TabId>('profile')

  // Permanent super admin gets everything
  const isSuperAdmin =
    localUser?.is_super_admin === true ||
    localUser?.email?.toLowerCase() === PERMANENT_SUPER_ADMIN_EMAIL

  const isTrial = localUser?.is_trial === true

  // Filter tabs based on role:
  // - Super admin: all tabs
  // - Trial user: only Profile + Security
  // - Regular authenticated user: everything except ADMIN_ONLY_TABS
  const visibleTabs = TABS.filter((tab) => {
    if (isSuperAdmin) return true
    if (isTrial) return !TRIAL_HIDDEN_TABS.has(tab.id)
    return !ADMIN_ONLY_TABS.has(tab.id)
  })

  // If the current active tab is not visible (e.g. user role changed), reset to profile
  const safeActiveTab = visibleTabs.some((t) => t.id === activeTab) ? activeTab : 'profile'

  const renderTab = () => {
    switch (safeActiveTab) {
      case 'profile': return <ProfileTab />
      case 'security': return <SecurityTab />
      case 'permissions': return isSuperAdmin ? <RbacSettingsTab /> : <ProfileTab />
      case 'users': return isSuperAdmin ? <UserManagementTab /> : <ProfileTab />
      case 'trialLicenses': return isSuperAdmin ? <TrialLicensesTab /> : <ProfileTab />
      case 'activity': return isSuperAdmin ? <ActivityTab /> : <ProfileTab />
      case 'integrations': return isSuperAdmin ? <IntegrationsTab /> : <ProfileTab />
      case 'database': return isSuperAdmin ? <DatabaseTab /> : <ProfileTab />
      case 'referenceData': return isSuperAdmin ? <ReferenceDataSettingsTab /> : <ProfileTab />
      case 'demoData': return isSuperAdmin ? <DemoDataSettingsTab /> : <ProfileTab />
      case 'sla': return isSuperAdmin ? <SlaSettingsTab /> : <ProfileTab />
      case 'platform': return isSuperAdmin ? <PlatformSettingsTab /> : <ProfileTab />
      default: {
        const _exhaustive: never = safeActiveTab
        return _exhaustive
      }
    }
  }

  const activeLabel = visibleTabs.find((t) => t.id === safeActiveTab)?.label || ''

  return (
    <div className="flex h-full px-4 py-3 gap-4">
      {/* Sidebar Navigation */}
      <div className="w-[200px] flex-shrink-0 space-y-1">
        <div className="mb-3 px-2">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-stone-600">Settings</div>
          <div className="text-[14px] font-semibold text-stone-100">{isSuperAdmin ? 'Administration' : isTrial ? 'My Account' : 'My Settings'}</div>
        </div>
        {visibleTabs.map((tab) => {
          const Icon = tab.icon
          const isActive = safeActiveTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-all duration-150 ${
                isActive
                  ? 'border-amber-400/25 bg-amber-400/[0.08] text-stone-100'
                  : 'border-transparent text-stone-400 hover:border-amber-400/10 hover:bg-white/[0.02] hover:text-stone-200'
              }`}
            >
              <Icon size={14} className="flex-shrink-0" />
              <span className="text-[12px] font-medium">{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Content Panel */}
      <div className="flex-1 min-w-0 overflow-y-auto rounded-lg border border-amber-400/10 bg-black/30">
        <div className="panel-header">
          <span className="panel-header-title">{activeLabel}</span>
        </div>
        <div className="p-4">
          {renderTab()}
        </div>
      </div>
    </div>
  )
}
